/*
 * Copyright (c) 2026 Ipivot Corporation.
 * All rights reserved.
 * This software is the confidential and proprietary information of
 *   Ipivot Corporation ("Confidential Information").
 */

/*
 * The single fingerprint map in the fingerprint editor: one marker for the
 * business location, with the countries the fingerprint claims picked out.
 *
 * Country matching runs in JS rather than in a MapLibre filter expression
 * because the caller decides what counts as a match (name normalization plus
 * an alias table).  The match is resolved once against the loaded outlines and
 * the result becomes a plain name filter on the highlight layer.
 */

import { useEffect, useState } from 'react';
import type { Map as MapLibreMap, GeoJSONSource } from 'maplibre-gl';
import BaseMap from './BaseMap';
import { loadMapData, COUNTRIES_50M } from '../utils/mapStyle';

type FingerGeoMapProps = {
    lat: number | null;
    lon: number | null;
    isCountryMatch: (geoName: string) => boolean;
    highlightColor: string;
    strokeColor: string;
};

function FingerGeoMap({ lat, lon, isCountryMatch, highlightColor, strokeColor }: FingerGeoMapProps) {
    const [ map, setMap ] = useState<MapLibreMap | null>(null);
    const hasCoords = lat !== null && lon !== null;

    useEffect(() => {
        if (!map) {
            return;
        }
        let cancelled = false;
        loadMapData(COUNTRIES_50M).then((countries) => {
            if (cancelled || !map.getLayer('country-fill')) {
                return;
            }
            const names = (countries.features || [])
                .map((f: any) => String(f?.properties?.name || ''))
                .filter((n: string) => n && isCountryMatch(n));

            if (!map.getLayer('country-highlight')) {
                map.addLayer({
                    id: 'country-highlight',
                    type: 'fill',
                    source: 'countries',
                    filter: [ 'in', [ 'get', 'name' ], [ 'literal', names ] ] as any,
                    paint: { 'fill-color': highlightColor }
                    // Under the borders, so outlines stay crisp over the fill
                }, map.getLayer('country-line') ? 'country-line' : undefined);
            } else {
                map.setFilter('country-highlight',
                    [ 'in', [ 'get', 'name' ], [ 'literal', names ] ] as any);
            }
        }).catch((e) => console.error('country highlight failed', e));

        return () => { cancelled = true; };
    }, [ map, isCountryMatch, highlightColor ]);

    useEffect(() => {
        if (!map || !hasCoords) {
            return;
        }
        const data = {
            type: 'FeatureCollection',
            features: [ {
                type: 'Feature',
                properties: {},
                geometry: { type: 'Point', coordinates: [ lon, lat ] }
            } ]
        };
        const existing = map.getSource('finger-point') as GeoJSONSource | undefined;
        if (existing) {
            existing.setData(data as any);
            return;
        }
        map.addSource('finger-point', { type: 'geojson', data } as any);
        // A halo plus the marker itself, matching the old SVG pair
        map.addLayer({
            id: 'finger-point-halo',
            type: 'circle',
            source: 'finger-point',
            paint: { 'circle-radius': 12, 'circle-color': 'rgba(255, 219, 77, 0.25)' }
        });
        map.addLayer({
            id: 'finger-point-dot',
            type: 'circle',
            source: 'finger-point',
            paint: {
                'circle-radius': 5,
                'circle-color': '#ffdb4d',
                'circle-stroke-width': 1,
                'circle-stroke-color': '#111'
            }
        });
    }, [ map, hasCoords, lat, lon ]);

    return (
        <BaseMap
            onMapReady={setMap}
            colors={{ stroke: strokeColor } as any}
            center={hasCoords ? [ lon as number, lat as number ] : [ 0, 20 ]}
            zoom={hasCoords ? 3 : 0.8}
            minZoom={0.5}
            maxZoom={14}
        />
    );
}

export default FingerGeoMap;
